import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES, QUOTE_WA } from "../lib/constants";
import SectionHeading from "../components/SectionHeading";
import ServiceIcon from "../components/ServiceIcon";

const HERO_IMG =
  "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1800&q=80";

const SERVICE_BENEFITS = {
  flights: ["Business & First fares", "Lounge access", "Multi-city itineraries"],
  visa: ["Tourist · Business · Transit", "Document review", "Embassy liaison"],
  hotels: ["Guaranteed late check-out", "Suite upgrades when available", "Direct billing"],
  cars: ["Chauffeur or self-drive", "Premium fleet", "Airport priority"],
  insurance: ["Medical & evacuation", "Trip cancellation", "Corporate liability"],
  corporate: ["Dedicated account manager", "Policy compliance", "24/7 support"],
  cruises: ["Suites & staterooms", "Private shore excursions", "Concierge planning"],
  holidays: ["Honeymoons & milestones", "Family safaris", "Wellness retreats"],
};

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Airport at sunrise" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070C] via-[#05070C]/80 to-[#05070C]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05070C]/30 via-transparent to-[#05070C]" />
        </div>
        <div className="relative container-x pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="eyebrow mb-6">
              <span className="hairline" /> Our Services
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              Eight services. <br />
              <span className="gold-text italic">One trusted desk.</span>
            </h1>
            <p className="mt-6 max-w-xl text-white/65">
              Every detail of your journey — orchestrated by a single team that
              never closes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services list */}
      <section className="section">
        <div className="container-x">
          <div className="space-y-px bg-white/5 border border-white/5">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6 }}
                data-testid={`service-row-${s.id}`}
              >
                <Link
                  to={`/services/${s.id}`}
                  data-testid={`service-row-link-${s.id}`}
                  className="grid lg:grid-cols-12 gap-6 lg:gap-12 bg-[#05070C] p-8 lg:p-12 hover:bg-[#0B1120] transition-colors duration-500 group"
                >
                  <div className="lg:col-span-1 flex lg:flex-col items-center lg:items-start justify-between lg:justify-start gap-4">
                    <span className="text-[11px] uppercase tracking-[0.3em] text-gold/70">
                      0{i + 1}
                    </span>
                    <span className="grid place-items-center h-12 w-12 rounded-full border border-gold/30 text-gold group-hover:border-gold transition-colors">
                      <ServiceIcon name={s.icon} className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="lg:col-span-4">
                    <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight group-hover:text-gold/95 transition-colors">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-xs uppercase tracking-[0.24em] text-gold/80">
                      {s.short}
                    </p>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="text-white/65 leading-relaxed">{s.description}</p>
                  </div>
                  <div className="lg:col-span-3 flex flex-col justify-between gap-6">
                    <ul className="space-y-3">
                      {(SERVICE_BENEFITS[s.id] || []).map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-3 text-sm text-white/75"
                        >
                          <Check className="h-4 w-4 text-gold mt-0.5 shrink-0" strokeWidth={2} />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.22em] opacity-70 group-hover:opacity-100 transition-opacity">
                      Enquire — WhatsApp or Email
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[#0B1120]/40 border-t border-white/5">
        <div className="container-x text-center">
          <SectionHeading
            eyebrow="Get Started"
            title="Tell us about your next journey."
            goldWord="journey."
            align="center"
          />
          <a
            href={QUOTE_WA}
            target="_blank"
            rel="noreferrer"
            data-testid="services-cta-quote"
            className="btn-gold mt-10"
          >
            Request a Quote on WhatsApp
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>
    </div>
  );
}
